import React, { useState } from 'react';
import styled from 'styled-components';
import { MdPushPin } from 'react-icons/md';
import { toast } from 'react-toastify';
import { usePinsApi } from '@/features/ai-agents/shared/use-pins-api';

export interface PinButtonProps {
  agentId: string;
  sessionId: string;
  messageId: string;
  isLocalHost?: boolean;
  className?: string;
}

export function PinButton({
  agentId,
  sessionId,
  messageId,
  isLocalHost = false,
  className,
}: PinButtonProps) {
  const { createPin, loading } = usePinsApi(isLocalHost);
  const [isPinned, setIsPinned] = useState(false);
  const onPinClicked = async (ev: React.MouseEvent) => {
    ev.stopPropagation();
    if (loading || isPinned) return;
    try {
      await createPin(agentId, sessionId, messageId);
      setIsPinned(true);
      toast.success(`Report pinned`);
    } catch {
      toast.error(`Failed to pin report`);
    }
  };

  if (!agentId || !sessionId || !messageId) {
    return null;
  }

  return (
    <Wrapper
      type="button"
      className={className}
      disabled={loading || isPinned}
      $isLoading={loading}
      onClick={onPinClicked}
    >
      {loading ? <ButtonSpinner /> : <MdPushPin size={14} />}
      {isPinned ? `Pinned` : `Pin`}
    </Wrapper>
  );
}

const ButtonSpinner = styled.span`
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: currentColor;
  animation: pin-spinner-spin 0.7s linear infinite;

  @keyframes pin-spinner-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Wrapper = styled.button<{ $isLoading?: boolean }>`
  appearance: none;
  outline: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.4rem;
  border-radius: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: white;
  color: #111111;
  font-family: Eesti, sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  white-space: nowrap;
  transform: scale(1);
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease,
    transform 0.15s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    background: #111111;
    color: white;
    border-color: #111111;
    transform: translateY(-1px) scale(1.03);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
    box-shadow: none;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
    transform: scale(1);
    box-shadow: none;
  }

  ${({ $isLoading }) => $isLoading && `cursor: progress;`}
`;
